import type { FormEvent } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/lib/base/buttons/button';
import { FormProvider, useFormContext } from '@/lib/base/forms/form-context';
import type { FieldConfig } from '@/lib/base/forms/hooks/useFormErrors';
import { AlertFloating } from '@/lib/base/overlays/alerts';
import { sanitizeError } from '@/lib/utils/errors';

import { useUser } from '../../allauth_auth';
import FormErrors from '../../components/FormErrors';
import { useOrg } from '../../contexts/OrgContext';
import { useTitle } from '../../hooks/useTitle';
import { changePassword } from '../../lib/allauth';
import type { AllauthResponse } from '../../types/allauth';
import { PasswordFormFields } from './components/PasswordFormFields';
import { makePasswordMatchValidator } from './components/passwordValidation';

// =============================================================================
// Types
// =============================================================================

type ResponseState = { fetching: boolean; content: AllauthResponse | null };

interface ChangePasswordFormProps {
  hasCurrentPassword: boolean;
  currentPassword: string;
  newPassword: string;
  newPassword2: string;
  setCurrentPassword: (v: string) => void;
  setNewPassword: (v: string) => void;
  setNewPassword2: (v: string) => void;
  response: ResponseState;
  setResponse: (updater: (prev: ResponseState) => ResponseState) => void;
  saveSuccess: boolean;
  setSaveSuccess: (v: boolean) => void;
}

// =============================================================================
// Inner form component
// =============================================================================

function ChangePasswordForm({
  hasCurrentPassword,
  currentPassword,
  newPassword,
  newPassword2,
  setCurrentPassword,
  setNewPassword,
  setNewPassword2,
  response,
  setResponse,
  saveSuccess,
  setSaveSuccess,
}: ChangePasswordFormProps) {
  const navigate = useNavigate();
  const { currentOrg } = useOrg();
  const { touchAll, isValid, errorSummary, scrollToField } = useFormContext();

  const profilePath = currentOrg
    ? `/${currentOrg.id}/profile`
    : '/dashboard/profile';

  const cancel = useCallback(() => {
    navigate(profilePath);
  }, [navigate, profilePath]);

  const onSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      touchAll();
      if (!isValid) {
        if (errorSummary.length > 0) {
          requestAnimationFrame(() => scrollToField(errorSummary[0].name));
        }
        return;
      }
      setSaveSuccess(false);
      setResponse(r => ({ ...r, fetching: true }));
      changePassword({
        current_password: currentPassword,
        new_password: newPassword,
      })
        .then(resp => {
          setResponse(r => ({ ...r, content: resp }));
          if (resp.status === 200) {
            setSaveSuccess(true);
            navigate(profilePath);
          }
        })
        .catch(err => {
          console.error(err);
          toast.error(sanitizeError(err));
        })
        .then(() => {
          setResponse(r => ({ ...r, fetching: false }));
        });
    },
    [
      touchAll,
      isValid,
      errorSummary,
      scrollToField,
      setSaveSuccess,
      setResponse,
      currentPassword,
      newPassword,
      profilePath,
      navigate,
    ]
  );

  return (
    <div className='min-h-full bg-bg-primary'>
      <div className='max-w-2xl mx-auto space-y-6 p-6 lg:p-8'>
        {/* Modern Page Header */}
        <div className='mb-8'>
          <div className='flex items-center gap-4 mb-4'>
            <Link
              to={profilePath}
              className='flex items-center text-tertiary hover:text-primary transition-colors'
            >
              <svg
                className='w-5 h-5 mr-2'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M15 19l-7-7 7-7'
                />
              </svg>
              Back to profile
            </Link>
          </div>
          <h1 className='text-2xl font-semibold text-primary mb-2'>
            {hasCurrentPassword ? 'Change password' : 'Set password'}
          </h1>
          <p className='text-tertiary'>
            {hasCurrentPassword
              ? 'Update your password to keep your account secure'
              : 'Set a password for your account to enhance security'}
          </p>
        </div>

        {/* Success Message */}
        {saveSuccess && (
          <AlertFloating
            color='success'
            title={`Password ${hasCurrentPassword ? 'changed' : 'set'} successfully.`}
            description='Redirecting…'
          />
        )}

        {/* Password Change Form */}
        <div className='bg-bg-primary rounded-xl border border-border-secondary shadow-sm overflow-hidden'>
          <div className='px-6 py-5 border-b border-border-secondary'>
            <h2 className='text-lg font-semibold text-primary'>
              {hasCurrentPassword ? 'Update password' : 'Create password'}
            </h2>
            <p className='text-sm text-tertiary mt-1'>
              {hasCurrentPassword
                ? 'Enter your current password and choose a new one'
                : 'Choose a strong password for your account'}
            </p>
          </div>

          <div className='p-6'>
            <form onSubmit={onSubmit} noValidate className='space-y-6'>
              {/* Global form errors */}
              <FormErrors errors={response.content?.errors} />

              <PasswordFormFields
                hasCurrentPassword={hasCurrentPassword}
                currentPassword={currentPassword}
                newPassword={newPassword}
                newPassword2={newPassword2}
                onCurrentPasswordChange={setCurrentPassword}
                onNewPasswordChange={setNewPassword}
                onNewPassword2Change={setNewPassword2}
                serverErrors={response.content?.errors}
              />

              {/* Action Buttons */}
              <div className='flex gap-3 pt-4'>
                <Button
                  type='submit'
                  isDisabled={response.fetching || saveSuccess}
                  color='primary'
                  size='md'
                  isLoading={response.fetching}
                  className='flex-1'
                >
                  {hasCurrentPassword ? 'Change password' : 'Set password'}
                </Button>
                <Button
                  type='button'
                  isDisabled={response.fetching}
                  onClick={cancel}
                  color='secondary'
                  size='md'
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Outer component — owns state, provides FormProvider
// =============================================================================

export default function ChangePassword() {
  useTitle('Change Password');
  const hasCurrentPassword = useUser().has_usable_password;
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPassword2, setNewPassword2] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [response, setResponse] = useState<ResponseState>({
    fetching: false,
    content: null,
  });

  const fields = useMemo(() => {
    const config: Record<string, FieldConfig> = {};
    if (hasCurrentPassword) {
      config.current_password = { required: true };
    }
    config.new_password = { required: true };
    config.new_password2 = {
      required: true,
      validate: makePasswordMatchValidator(newPassword),
    };
    return config;
  }, [newPassword, hasCurrentPassword]);

  const values = useMemo(() => {
    const vals: Record<string, unknown> = {};
    if (hasCurrentPassword) {
      vals.current_password = currentPassword;
    }
    vals.new_password = newPassword;
    vals.new_password2 = newPassword2;
    return vals;
  }, [currentPassword, newPassword, newPassword2, hasCurrentPassword]);

  return (
    <FormProvider fields={fields} values={values}>
      <ChangePasswordForm
        hasCurrentPassword={hasCurrentPassword}
        currentPassword={currentPassword}
        newPassword={newPassword}
        newPassword2={newPassword2}
        setCurrentPassword={setCurrentPassword}
        setNewPassword={setNewPassword}
        setNewPassword2={setNewPassword2}
        response={response}
        setResponse={setResponse}
        saveSuccess={saveSuccess}
        setSaveSuccess={setSaveSuccess}
      />
    </FormProvider>
  );
}
